import React, { useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { cloneDeep } from 'lodash'

import {
  Grid, Button, IconButton, Typography, Tooltip, Popover,
  Accordion, AccordionDetails, AccordionSummary, Paper,
  makeStyles, CircularProgress
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'

import { ExpandMore as ExpandMoreIcon, Delete as DeleteIcon, FlashOn as FlashOnIcon } from '@material-ui/icons'

import { setFilter } from '../../../../redux/actions/select'
import { deleteFilter, getUserFilters, updateFastFilter } from '../../../../redux/actions'
import { NotificationToast } from '../../tools/Toast'

import { defaultFilter } from '../../../../utils/variables'
import { GET_USER_FILTERS } from '../../../../redux/types'
import { usePromiseTracker } from 'react-promise-tracker'

import moment from 'moment'

const useStyles = makeStyles((theme) => ({
  filterAccordion: {
    width: '100%'
  },
  filterRow: {
    marginBottom: '1rem'
  },
  filterTitle: {
    color: '#3984F7',
    fontWeight: '700',
    fontSize: '18px'
  },
  filterDetailsTitle: {
    color: '#3984F7',
    fontWeight: '400',
    fontSize: '13px',
    textTransform: 'uppercase'
  },
  filterDetailsSubtitle: {
    color: '#3984F7',
    fontWeight: '400',
    fontSize: '11px'
  },
  filterDetailsText: {
    color: 'black',
    fontWeight: '400',
    fontSize: '13px'
  },
  filterDetailsSubtext: {
    color: 'black',
    fontWeight: '400',
    fontSize: '11px'
  },
  deleteIcon: {
    color: '#B3B3B3'
  },
  flashOnIcon: {
    color: '#B3B3B3',
    transition: '0.5s'
  },
  activeFlashOnIcon: {
    color: '#f5d818',
    transition: '0.5s'
  },
  applyButton: {
    backgroundColor: '#3984F7',
    color: 'white',
    height: '30px',
    width: '100px',
    '&:hover': {
      backgroundColor: '#2469d2'
    }
  },
  editButton: {
    marginRight: '1.5rem',
    color: 'grey'
  },
  filterContainer: {
    maxHeight: '70vh',
    overflow: 'scroll'
  }
}))

const frequencyInfo = {
  0: { title: 'Sunday' },
  1: { title: 'Monday' },
  2: { title: 'Tuesday' },
  3: { title: 'Wednesday' },
  4: { title: 'Thursday' },
  5: { title: 'Friday' },
  6: { title: 'Saturday' },
  H: 'Hours',
  D: 'Days',
  W: 'Weeks',
  M: 'Months'
}

const UserFilters = (props) => {
  const {
    setFilterForm, setFormData, FilterFormTypes,
    setIsEditing, setFilterCheckObject,
    DefaultView, toggleFilterModal,
    setStartDate, setEndDate, setStartTime, setEndTime, setDateSelectType,
    openFilter, setOpenFilter, setSubOptions, setEditedSubOptions, setVal = () => {}
  } = props
  const deepDefaultFilter = cloneDeep(defaultFilter)

  const { FILTER_OPTIONS } = FilterFormTypes

  const classes = useStyles()
  const dispatch = useDispatch()

  const { promiseInProgress } = usePromiseTracker({ area: 'updating-fast-filter' })

  const loadingState = useSelector(state => state.loadingState)
  const { type, loading } = loadingState

  const UserFilters = useSelector(state => state.filters.filters)
  const [deleteID, setDeleteID] = useState('')

  const [confirmAnchorEl, setConfirmAnchorEl] = useState(null)
  const handleConfirmPopover = (e) => {
    setConfirmAnchorEl(e.currentTarget)
  }
  const handleConfirmPopClose = () => {
    setConfirmAnchorEl(null)
  }
  const openConfirmPopover = Boolean(confirmAnchorEl)

  const updatingFilterID = useRef(null)

  const handleToggleFastFilter = (e, filter) => {
    e.preventDefault()
    e.stopPropagation()
    const { filterID, isFastFilter } = filter
    updatingFilterID.current = filterID
    const newIsFF = isFastFilter ? 0 : 1

    dispatch(updateFastFilter(filterID, newIsFF, toggleFFSuccess, toggleFFFailure))
  }

  const toggleFFSuccess = () => {
    dispatch(getUserFilters())
    updatingFilterID.current = null
  }

  const toggleFFFailure = () => {
    NotificationToast(true, 'Unable to toggle Fast Filter')
    updatingFilterID.current = null
  }

  // Apply users saved filter from My Filters (filter needs adjustingt o match ES filter)
  const handleApply = (filter) => {
    const formattedFilter = {
      ...deepDefaultFilter,
      fastFilterID: filter.isFastFilter ? filter.filterID : '',
      captureAreas: filter.statuses == null ? [] : filter.statuses,
      categories: filter.categories === null ? [] : filter.categories,
      endDate: filter.endDate,
      endTime: filter.endTime,
      severityMax: filter.severityMax,
      severityMin: filter.severityMin,
      source: filter.capture_source === 2 ? 'all' : filter.capture_source,
      startDate: filter.startDate,
      startTime: filter.startTime,
      usernames: filter.users == null ? [] : filter.users,
      workstations: filter.workstations == null ? [] : filter.workstations,
      refresh: true
    }
    dispatch(setFilter(formattedFilter))

    props.history.push({
      pathname: '/captures',
      search: DefaultView === 'L'
        ? '?view=list&page=1&activeCapture=0'
        : DefaultView === 'G'
          ? '?view=grid&page=1&activeCaptures=[]'
          : '?view=single&capture=1'
    })
    toggleFilterModal()
  }

  // Apply saved filter to filter options form for edit
  const handleEdit = (filter) => {
    // Check if start and end date are same date to set single date picker
    if (moment(filter.startDate).format('L') === moment(filter.endDate).format('L')) {
      setDateSelectType('single')
    }

    const formattedFilter = {
      ...deepDefaultFilter,
      filterName: filter.filterName,
      filterID: filter.filterID,
      isFastFilter: filter.isFastFilter,
      captureAreas: filter.statuses == null ? [] : filter.statuses,
      categories: filter.categories === null ? [] : filter.categories,
      endDate: filter.endDate !== 0 ? moment(filter.endDate) : filter.endDate,
      endTime: filter.endTime !== 0 ? moment(filter.endTime) : filter.endTime,
      severityMax: filter.severityMax,
      severityMin: filter.severityMin,
      source: filter.capture_source === 2 ? 'all' : filter.capture_source,
      startDate: filter.startDate !== 0 ? moment(filter.startDate) : filter.startDate,
      startTime: filter.startTime !== 0 ? moment(filter.startTime) : filter.startTime,
      usernames: filter.users == null ? [] : filter.users,
      workstations: filter.workstations == null ? [] : filter.workstations
    }

    const formatSubStartTime = filter.subStartDate && typeof filter.subStartDate === 'number' ? moment.unix(filter.subStartDate) : null

    const subOptionsFilter = {
      subEnabled: Boolean(filter.subEnabled),
      subFrequency: filter.subFrequency || 0,
      subFrequencyType: filter.subFrequencyType || 'W',
      subStartTime: formatSubStartTime && Boolean(formatSubStartTime.hour()) ? formatSubStartTime : null,
      subStartDate: formatSubStartTime,
      subSendEmailOn: filter.subSendEmailOn,
      subEndDate: filter.subEndDate && typeof filter.subEndDate === 'number' ? moment.unix(filter.subEndDate) : null,
      subEndLimit: filter.subEndLimit,
      subFormat: filter.subFormat || 'CSV'
    }

    setIsEditing(true)
    setFilterForm(FILTER_OPTIONS)

    // Set date time displays if timestamps are not equal to 0
    if (filter.startDate !== 0) { setStartDate(moment(filter.startDate)) } else { setStartDate(null) }
    if (filter.endDate !== 0) { setEndDate(moment(filter.endDate)) } else { setEndDate(null) }
    if (filter.startTime !== 0) { setStartTime(moment(filter.startTime)) } else { setStartTime(null) }
    if (filter.endTime !== 0) { setEndTime(moment(filter.endTime)) } else { setEndTime(null) }

    // Set main filter form to saved filter for editing
    setFormData(formattedFilter)
    setSubOptions({ ...subOptionsFilter })

    // set any users saved in the filter to the side menu
    if (filter.users) { setVal(filter.users) }

    // Set separate object containing saved filter data to check if changes have been made
    setFilterCheckObject(formattedFilter)
    setEditedSubOptions({ ...subOptionsFilter })
  }

  // Use saved filter ID to delete that filter
  const handleDelete = (filterID) => {
    dispatch(deleteFilter(filterID, fireDeleteSuccess, fireDeleteFailure))
  }

  const fireDeleteSuccess = () => {
    setDeleteID('')
    handleConfirmPopClose()
    NotificationToast(false, 'Filter Successfully Deleted!')
    dispatch(getUserFilters())
  }

  const fireDeleteFailure = () => {
    setDeleteID('')
    handleConfirmPopClose()
    NotificationToast(true, 'Unable to Delete Filter.')
  }

  // Get status display text
  const getStatus = (status) => {
    if (status === 'P') return 'Problem'
    if (status === 'N') return 'Not A Problem'
    if (status === 'H') return 'Hold'
    if (status === 'S') return 'Resolved'
    if (status === 'U') return 'Unviewed'
    if (status === 'Flagged') return status
  }

  // Get source display text
  const getSource = (source) => {
    if (source === 0) return 'Keyboard'
    if (source === 1) return 'Application'
    if (source === 2) return 'All'
  }

  return (
    <Grid container direction='row'>
      <Grid container item direction='column' xs={12} style={{ padding: '1rem' }}>

        <Grid item container direction='row' alignItems='center' style={{ marginBottom: '1rem' }}>
          <Typography
            className='font-weight-bold'
            style={{ marginRight: '.5rem' }}
            variant='h6'
          >
            My Filters
          </Typography>
          <Typography>({UserFilters && UserFilters.length ? UserFilters.length : 0})</Typography>
        </Grid>

        {
          type === GET_USER_FILTERS && loading === true && !updatingFilterID.current
            ? (
              <>
                <Grid item container direction='row'>
                  <Skeleton animation='wave' width='100%' height={100} />
                </Grid>

                <Grid item container direction='row'>
                  <Skeleton animation='wave' width='100%' height={100} />
                </Grid>

                <Grid item container direction='row'>
                  <Skeleton animation='wave' width='100%' height={100} />
                </Grid>
              </>
              )
            : (
              <>
                {UserFilters && Boolean(UserFilters.length)
                  ? (
                    <Grid item container direction='row' justify='center' className={classes.filterContainer}>
                      <Grid container item direction='column'>

                        {
                          UserFilters.map((filter) => {
                            return (
                              <Grid
                                item
                                container
                                direction='row'
                                justifycontent='center'
                                alignItems='center'
                                key={`saved-filter-${filter.filterID}`}
                                className={classes.filterRow}
                              >
                                <Accordion
                                  elevation={3}
                                  className={classes.filterAccordion}
                                  expanded={openFilter === filter.filterID}
                                  onChange={(e, expanded) => { expanded === true ? setOpenFilter(filter.filterID) : setOpenFilter('') }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-label='Expand'
                                  >
                                    <Grid item container direction='row' alignItems='center'>
                                      <Grid item xs={1}>
                                        <Tooltip title='Delete Filter' placement='top' arrow enterDelay={500} enterNextDelay={500}>
                                          <IconButton onClick={(e) => { e.stopPropagation(); handleConfirmPopover(e); setDeleteID(filter.filterID) }}>
                                            <DeleteIcon className={classes.deleteIcon} />
                                          </IconButton>
                                        </Tooltip>
                                      </Grid>
                                      <Grid item xs={7}>
                                        <Tooltip title={`${filter.filterName}`} placement='top-start' arrow enterDelay={500} enterNextDelay={500}>
                                          <Typography className={classes.filterTitle}>{filter.filterName}</Typography>
                                        </Tooltip>
                                      </Grid>
                                      <Grid item xs={1}>
                                        {
                                          promiseInProgress && updatingFilterID.current === filter.filterID
                                            ? (<CircularProgress size={25} thickness={6} style={{ color: '#B3B3B3' }} />)
                                            : (
                                              <Tooltip title='Set as Fast Filter' placement='top' arrow enterDelay={500} enterNextDelay={500}>
                                                <IconButton style={{ outline: 'none' }} onClick={(e) => { handleToggleFastFilter(e, filter) }} disableRipple size='small'>
                                                  <FlashOnIcon className={filter.isFastFilter ? classes.activeFlashOnIcon : classes.flashOnIcon} />
                                                </IconButton>
                                              </Tooltip>
                                              )
                                      }
                                      </Grid>
                                      <Grid item container justify='flex-end' xs={1}>
                                        <Button onClick={(e) => { e.stopPropagation(); handleEdit(filter) }} className={classes.editButton}>Edit</Button>
                                      </Grid>
                                      <Grid item xs={1}>
                                        <Button onClick={(e) => { e.stopPropagation(); handleApply(filter) }} className={classes.applyButton}>Apply</Button>
                                      </Grid>
                                    </Grid>
                                  </AccordionSummary>
                                  <AccordionDetails>

                                    <Grid container direction='row'>

                                      <Grid item xs>
                                        {/* COLUMN 1 */}
                                        <Grid item container direction='column'>

                                          <Grid item container direction='row'>

                                            {/* SEVERITY */}
                                            <Grid item xs>
                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsTitle}>
                                                  SEVERITY:
                                                </Typography>
                                              </Grid>

                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                  {filter.severityMin && filter.severityMax ? `${filter.severityMin} - ${filter.severityMax}` : '1 - 100'}
                                                </Typography>
                                              </Grid>
                                            </Grid>

                                            {/* SOURCES */}
                                            <Grid item xs>
                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsTitle}>
                                                  SOURCES:
                                                </Typography>
                                              </Grid>

                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                  {filter.capture_source || filter.capture_source === 0 ? getSource(filter.capture_source) : 'All'}
                                                </Typography>
                                              </Grid>
                                            </Grid>

                                            {/* TIME RANGE */}
                                            <Grid item xs>
                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsTitle}>
                                                  TIME RANGE:
                                                </Typography>
                                              </Grid>

                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                  {filter.startTime && filter.endTime ? `${moment(filter.startTime).format('hh:mm a')} - ${moment(filter.endTime).format('hh:mm a')}` : 'All'}
                                                </Typography>
                                              </Grid>
                                            </Grid>

                                          </Grid>

                                          <Grid item container direction='row'>

                                            {/* CATEGORIES */}
                                            <Grid item xs>
                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsTitle}>
                                                  CATEGORIES:
                                                </Typography>
                                              </Grid>

                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                  {
                                                    filter.categories && (filter.categories.length || filter.categories !== null)
                                                      ? (
                                                          filter.categories.map(category =>
                                                            <Grid item key={category} style={{ marginBottom: '.2rem' }}>
                                                              <Typography
                                                                variant='caption'
                                                                gutterBottom
                                                                className={classes.filterDetailsText}
                                                              >
                                                                {category}
                                                              </Typography>
                                                            </Grid>)
                                                        )
                                                      : (
                                                        <Grid item>
                                                          <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                            All
                                                          </Typography>
                                                        </Grid>
                                                        )
                                                  }
                                                </Typography>
                                              </Grid>
                                            </Grid>

                                            {/* USERS */}
                                            <Grid item xs>
                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsTitle}>
                                                  USERS:
                                                </Typography>
                                              </Grid>

                                              <Grid item>
                                                {
                                                  filter.users && (filter.users.length || filter.users !== null)
                                                    ? (
                                                        filter.users.map(user =>
                                                          <Grid item key={user} style={{ marginBottom: '.2rem' }}>
                                                            <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                              {user}
                                                            </Typography>
                                                          </Grid>
                                                        )
                                                      )
                                                    : (
                                                      <Grid item>
                                                        <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                          All
                                                        </Typography>
                                                      </Grid>
                                                      )
                                                }
                                              </Grid>
                                            </Grid>

                                            {/* DATE RANGE */}
                                            <Grid item xs>
                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsTitle}>
                                                  DATE RANGE:
                                                </Typography>
                                              </Grid>

                                              <Grid item>
                                                <Typography variant='caption' className={classes.filterDetailsText}>
                                                  {filter.startDate && filter.endDate && moment(filter.startDate).format('L') !== moment(filter.endDate).format('L') ? `${moment(filter.startDate).format('MM/DD/YYYY')} - ${moment(filter.endDate).format('MM/DD/YYYY')}` : filter.startDate && filter.endDate && moment(filter.startDate).format('L') === moment(filter.endDate).format('L') ? `${moment(filter.startDate).format('MM/DD/YYYY')}` : 'All'}
                                                </Typography>
                                              </Grid>
                                            </Grid>

                                          </Grid>

                                          <Grid item container direction='row'>

                                            {/* CAPTURE AREAS */}
                                            <Grid item xs={4}>
                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsTitle}>
                                                  CAPTURE AREAS:
                                                </Typography>
                                              </Grid>

                                              <Grid item>
                                                <Typography variant='caption' className={classes.filterDetailsText}>
                                                  {
                                                    filter.statuses && (filter.statuses.length || filter.statuses !== null)
                                                      ? (
                                                          filter.statuses.map((status) =>
                                                            <Grid item container direction='row' key={status} style={{ marginBottom: '.2 rem' }}>
                                                              <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                                {getStatus(status)}
                                                              </Typography>
                                                            </Grid>
                                                          )
                                                        )
                                                      : (
                                                        <Grid item container direction='row'>
                                                          <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                            All
                                                          </Typography>
                                                        </Grid>
                                                        )
                                                  }
                                                </Typography>
                                              </Grid>
                                            </Grid>

                                            {/* STATIONS */}
                                            <Grid item xs={3}>
                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsTitle}>
                                                  STATIONS:
                                                </Typography>
                                              </Grid>

                                              <Grid item>
                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                  {
                                                      filter.workstations && (filter.workstations.length || filter.workstations !== null)
                                                        ? (
                                                            filter.workstations.map((workstation) =>
                                                              <Grid container item direction='row' key={workstation} style={{ marginBottom: '.2 rem' }}>
                                                                <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                                  {workstation}
                                                                </Typography>
                                                              </Grid>
                                                            )
                                                          )
                                                        : (
                                                          <Grid item container direction='row'>
                                                            <Typography variant='caption' gutterBottom className={classes.filterDetailsText}>
                                                              All
                                                            </Typography>
                                                          </Grid>
                                                          )
                                                  }
                                                </Typography>
                                              </Grid>
                                            </Grid>

                                          </Grid>
                                        </Grid>

                                      </Grid>

                                      <Grid item xs>
                                        {/* COLUMN 2 */}
                                        <Grid container direction='column'>

                                          <Grid item>
                                            <Typography variant='caption' gutterBottom className={classes.filterDetailsTitle}>
                                              SUBSCRIPTION:
                                            </Typography>
                                          </Grid>

                                          <Grid item container direction='row'>

                                            <Grid item xs>
                                              <Grid container direction='row'>
                                                <Grid container item direction='column'>
                                                  <Grid item>
                                                    <Typography variant='caption' gutterBottom className={classes.filterDetailsSubtitle}>
                                                      Starting:
                                                    </Typography>
                                                  </Grid>

                                                  <Grid item>
                                                    <Typography variant='caption' gutterBottom className={classes.filterDetailsSubtext}>
                                                      {
                                                        filter.subEnabled ? moment.unix(filter.subStartDate).format('MM-DD-YYYY') : 'None'
                                                      }
                                                    </Typography>
                                                  </Grid>
                                                </Grid>
                                              </Grid>
                                            </Grid>

                                            <Grid item xs>
                                              <Grid container direction='row'>
                                                <Grid container item direction='column'>
                                                  <Grid item>
                                                    <Typography variant='caption' gutterBottom className={classes.filterDetailsSubtitle}>
                                                      Repeat On:
                                                    </Typography>
                                                  </Grid>

                                                  <Grid item>
                                                    <Typography variant='caption' gutterBottom className={classes.filterDetailsSubtext}>
                                                      {
                                                        filter.subEnabled && filter.subSendEmailOn ? frequencyInfo[filter.subSendEmailOn].title : 'None'
                                                      }
                                                    </Typography>
                                                  </Grid>
                                                </Grid>
                                              </Grid>
                                            </Grid>

                                          </Grid>

                                          <Grid item container direction='row'>

                                            <Grid item xs>
                                              <Grid container direction='row'>
                                                <Grid container item direction='column'>
                                                  <Grid item>
                                                    <Typography variant='caption' gutterBottom className={classes.filterDetailsSubtitle}>
                                                      Repeat Every:
                                                    </Typography>
                                                  </Grid>

                                                  <Grid item>
                                                    <Typography variant='caption' gutterBottom className={classes.filterDetailsSubtext}>
                                                      {
                                                        filter.subEnabled ? `${filter.subFrequency} ${frequencyInfo[filter.subFrequencyType]}` : 'None'
                                                      }
                                                    </Typography>
                                                  </Grid>
                                                </Grid>
                                              </Grid>
                                            </Grid>

                                            <Grid item xs>
                                              <Grid container direction='row'>
                                                <Grid container item direction='column'>
                                                  <Grid item>
                                                    <Typography variant='caption' gutterBottom className={classes.filterDetailsSubtitle}>
                                                      End:
                                                    </Typography>
                                                  </Grid>

                                                  <Grid item>
                                                    <Typography variant='caption' gutterBottom className={classes.filterDetailsSubtext}>
                                                      {
                                                        filter.subEnabled && (filter.subEndDate || filter.subEndLimit)
                                                          ? filter.subEndDate
                                                              ? moment.unix(filter.subEndDate).format('MM-DD-YYYY')
                                                              : `${filter.subEndLimit} Occurences`
                                                          : 'None'
                                                      }
                                                    </Typography>
                                                  </Grid>
                                                </Grid>
                                              </Grid>
                                            </Grid>

                                          </Grid>
                                        </Grid>

                                      </Grid>
                                    </Grid>

                                  </AccordionDetails>
                                </Accordion>

                                {/* CONFIRMATION POPOVER FOR DELETE (START) */}
                                <Popover
                                  id={openConfirmPopover ? 'confirm-popover' : undefined}
                                  open={openConfirmPopover}
                                  anchorEl={confirmAnchorEl}
                                  onClose={handleConfirmPopClose}
                                  anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                  }}
                                  transformOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right'
                                  }}
                                >
                                  <Grid container direction='column' style={{ padding: '1em' }}>
                                    <Typography
                                      style={{ fontSize: '.9rem', fontWeight: 'bold' }}
                                      gutterBottom
                                    >
                                      Are you sure you want to delete this filter?
                                    </Typography>

                                    <Grid item container direction='row' justify='flex-end' alignItems='center' style={{ marginTop: '.5em' }}>
                                      <Button
                                        onClick={handleConfirmPopClose}
                                        style={{ marginRight: '.3em', textTransform: 'none', fontWeight: '600', color: '#98B556' }}
                                      >
                                        Cancel
                                      </Button>

                                      <Button
                                        style={{ textTransform: 'none', fontWeight: '600', color: '#3984F7' }}
                                        onClick={(e) => { e.preventDefault(); handleDelete(deleteID) }}
                                      >
                                        Confirm
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Popover>
                                {/* CONFIRMATION POPOVER FOR DELETE (END) */}
                              </Grid>
                            )
                          })
                        }

                      </Grid>
                    </Grid>
                    )
                  : (
                    <Grid item container direction='row' justify='center' className='mt-5'>
                      <Paper elevation={3} style={{ width: '70%', height: '18rem' }}>
                        <Grid item container alignItems='center' style={{ height: '100%' }}>
                          <Grid item container justify='center' alignItems='center'>
                            <Grid item container direction='row' justify='center'>
                              <Typography
                                gutterBottom
                                style={{
                                  textTransform: 'none',
                                  fontWeight: 'bold',
                                  color: '#97B557',
                                  fontSize: '2rem'
                                }}
                              >
                                No Filters Saved
                              </Typography>
                            </Grid>

                            <Grid item container direction='row' justify='center'>
                              <Typography>Click </Typography>
                              <Typography style={{ color: '#3984F7', fontWeight: 'bold', marginLeft: '.4rem', marginRight: '.4rem' }}>SAVE TO MY FILTERS</Typography>
                              <Typography> after creating a filter.</Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                    )}
              </>
              )
        }
      </Grid>
    </Grid>
  )
}

export default withRouter(UserFilters)
